import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { SessionService } from './auth-session.service';
import { HttpService } from '@/common/http/http.service';
import { ApiResponseInterface } from './auth.interface';
import { TranslateService } from '@ngx-translate/core';
import { HTTP_ENDPOINTS } from '@/common/http/http.endpoints';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	// The token refresh in flight, shared by every request that gets a 401 while it runs
	private refresh$: Observable<string> | null = null;
	private translate?: TranslateService;
	private router?: Router;

	constructor(
		private sessionService: SessionService,
		private http: HttpService,
		private injector: Injector
	) {}

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		// exclude the login 401 error
		if (request.headers.get('X-Login-Request') === 'true') {
			// Bypass interceptor logic and forward the request
			return next
				.handle(request)
				.pipe(
					catchError((error: HttpErrorResponse) => {
						if (error.status === 401) {
							return of(new HttpResponse({
								status: 401,
								body: error.error
							}));
						}
						// For other errors, just pass them along
						return throwError(() => error);
					})
				);
		}

		// The refresh request sends the refresh token as its bearer token (HttpService.useRefreshToken()),
		// so it must not get the access token, and its errors end the session in handle401Error
		if (request.url.endsWith(HTTP_ENDPOINTS.auth.refresh)) {
			return next.handle(request);
		}

		// Add auth headers to all non-login requests
		const authRequest = this.addAuthorizationHeader(request);

		// handle all requests
		return next.handle(authRequest).pipe(
			catchError(error => {
				if (error instanceof HttpErrorResponse && error.status === 401) {
					return this.handle401Error(request, next);
				}
				return this.handleForbidden(error);
			})
		);
	}

	// Forbidden means the user may not do this, not that the session is over:
	// keep the session and hand the API's message to the caller, like a 422.
	// The answer carries the status, as HttpService.handleError's errors do, so a
	// page can tell "no permission" from other refusals. A body that isn't JSON
	// (a proxy's HTML page) is left out.
	private handleForbidden(error: unknown): Observable<HttpEvent<unknown>> {
		if (error instanceof HttpErrorResponse && error.status === 403) {
			const body = typeof error.error === 'object' && error.error !== null ? error.error : {};
			return of(new HttpResponse({
				status: 403,
				body: { ...body, success: false, status: 403 }
			}));
		}
		return throwError(() => error);
	}

	private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		// inject the services
		this.translate = this.translate || this.injector.get(TranslateService);
		this.router = this.router || this.injector.get(Router);

		if (!this.sessionService.getRefreshToken()) {
			return this.endSession('generic.auth.missingRefreshToken');
		}

		this.refresh$ ??= this.refreshTokens();

		return this.refresh$.pipe(
			catchError(() => this.endSession('generic.auth.invalidRefreshToken')),
			// retry the request with the new access token
			switchMap(() => next
				.handle(this.addAuthorizationHeader(request))
				.pipe(catchError(error => {
					// a 401 even with the new token means the session can't be recovered
					if (error instanceof HttpErrorResponse && error.status === 401) {
						return this.endSession('generic.auth.sessionRejected');
					}
					return this.handleForbidden(error);
				}))
			)
		);
	}

	private refreshTokens(): Observable<string> {
		return this
			.http
			.useRefreshToken()
			.post(HTTP_ENDPOINTS.auth.refresh, null)
			.pipe(
				map((response: ApiResponseInterface) => {
					if (!response?.success) {
						throw new Error(response?.message);
					}
					const { access_token, refresh_token } = response.data;
					// update token session
					this.sessionService.updateTokenSession(access_token, refresh_token);
					return access_token;
				}),
				finalize(() => this.refresh$ = null),
				shareReplay(1)
			);
	}

	private endSession(messageKey: string): Observable<never> {
		this.sessionService.invalidateSession();
		this.router?.navigate(['/login']);
		return throwError(() => new Error(this.translate?.instant(messageKey)));
	}

	private addAuthorizationHeader(request: HttpRequest<unknown>) {
		const authHeaders = this.sessionService.getAuthHeaders();
		return request.clone({
			setHeaders: authHeaders
		});
	}
}
